import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import _ from "lodash";
import deviceServices from "app/services/automations/device-services";
import TimeSeriesChart from "app/shared/charts/TimeSeriesChart";
import ActuatorControlSection from "./ActuatorControlSection";
import { formatMeasurementValue, formatTimeAgo, isReadingStale } from "./deviceDisplay";

const RANGE_OPTIONS = [
    { value: "last_6h", label: "6 h" },
    { value: "last_24h", label: "24 h" },
    { value: "last_7d", label: "7 d" },
    { value: "last_30d", label: "30 d" },
];

const BUCKET_LABELS = {
    minute: "minuto",
    hour: "hora",
    day: "día",
    week: "semana",
    month: "mes",
};

// Cómo resume cada punto del gráfico su intervalo. Lo decide el backend según la variable: los conteos (entradas,
// salidas) se suman, los acumulados (energía) muestran cuánto subieron y el resto se promedia.
const AGGREGATION_LABELS = {
    avg: "promedio",
    sum: "total",
    increase: "aumento",
};

// Las variables de texto (estado, modo del termostato) no se grafican: se listan sus últimos cambios, que es lo que
// interesa de ellas.
const renderTextTimeline = (variable) => {
    const changes = _.uniqBy([...(variable.data || [])].reverse(), "y").slice(0, 6);
    if (!changes.length) return <Typography variant="caption" color="text.secondary">Sin datos en este rango</Typography>;

    return (
        <Stack spacing={0.5}>
            {changes.map((point) => (
                <Stack key={`${variable.field}-${point.x}`} direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 92 }}>
                        {formatTimeAgo(point.x) || "-"}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>{point.y}</Typography>
                </Stack>
            ))}
        </Stack>
    );
};

const DeviceDetailPanel = ({ deviceId, onClose, onEditDevice, onOpenAlerts }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [device, setDevice] = useState(null);
    const [result, setResult] = useState(null);
    const [range, setRange] = useState("last_24h");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const open = !!deviceId;

    useEffect(() => {
        if (!deviceId) {
            setDevice(null);
            setResult(null);
            setError(null);
            setRange("last_24h");
        }
    }, [deviceId]);

    useEffect(() => {
        if (!deviceId) return;
        let active = true;

        deviceServices.getDevice(deviceId, "?include=store,device_type.measurement_types,latest_device_metric")
            .then((response) => { if (active) setDevice(response?.data || response); })
            .catch(() => { if (active) setDevice(null); });

        return () => { active = false; };
    }, [deviceId]);

    const loadSeries = useCallback(() => {
        if (!deviceId) return undefined;
        let active = true;

        setLoading(true);
        setError(null);
        deviceServices.getDeviceSeries(deviceId, range)
            .then((response) => { if (active) setResult(response); })
            .catch(() => { if (active) setError("No se pudieron cargar las mediciones."); })
            .finally(() => { if (active) setLoading(false); });

        return () => { active = false; };
    }, [deviceId, range]);

    useEffect(() => loadSeries(), [loadSeries]);

    const latestTime = device?.latest_device_metric?.time;
    const stale = isReadingStale(latestTime);
    const variables = result?.variables || [];
    const bucketLabel = BUCKET_LABELS[result?.bucket];
    const summaryLabel = (variable) => (
        bucketLabel && `${AGGREGATION_LABELS[variable.aggregation] || AGGREGATION_LABELS.avg} por ${bucketLabel}`
    );

    return (
        <Drawer
            anchor={isMobile ? "bottom" : "right"}
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: "100%", md: 520 },
                    height: { xs: "92%", md: "100%" },
                    borderTopLeftRadius: { xs: 16, md: 0 },
                    borderTopRightRadius: { xs: 16, md: 0 },
                },
            }}>
            <Box sx={{ p: { xs: 1.5, md: 2 }, display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.25 }} noWrap>
                            {device?.name || "Dispositivo"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {_.compact([device?.store?.name, device?.zone || "Sin zona"]).join(" · ")}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose}><CloseOutlinedIcon fontSize="small" /></IconButton>
                </Stack>

                <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
                    {device?.kind && <Chip size="small" variant="outlined" label={device.kind} />}
                    {device?.code && <Chip size="small" variant="outlined" label={device.code} />}
                    {device?.device_type && (
                        <Chip size="small" variant="outlined" label={_.compact([device.device_type.brand, device.device_type.model]).join(" ")} />
                    )}
                    <Chip
                        size="small"
                        color={stale ? "warning" : "default"}
                        variant={stale ? "filled" : "outlined"}
                        label={latestTime ? `Última lectura ${formatTimeAgo(latestTime)}` : "Sin lecturas"}
                    />
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" variant="outlined" startIcon={<SettingsOutlinedIcon />}
                            onClick={() => onEditDevice?.(device?.id)} sx={{ textTransform: "none" }}>
                        Editar
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<NotificationsActiveOutlinedIcon />}
                            onClick={() => onOpenAlerts?.(device)} sx={{ textTransform: "none" }}>
                        Sus automatizaciones
                    </Button>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={range}
                        onChange={(_event, next) => { if (next) setRange(next); }}
                        sx={{ "& .MuiToggleButton-root": { px: 1.2, py: 0.4, textTransform: "none" } }}>
                        {RANGE_OPTIONS.map((option) => (
                            <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
                    {device?.controllable && (
                        <Box sx={{ mb: 1.25 }}><ActuatorControlSection deviceId={device.id} /></Box>
                    )}
                    {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

                    {loading && !variables.length && (
                        <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={28} /></Box>
                    )}

                    {!loading && !variables.length && !error && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                            Este dispositivo no tiene variables con historial.
                        </Typography>
                    )}

                    <Stack spacing={1.25} sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 150ms ease" }}>
                        {variables.map((variable) => (
                            <Paper key={variable.field} variant="outlined" sx={{ p: 1.25, borderRadius: 2.5 }}>
                                <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
                                    <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{variable.label}</Typography>
                                        {variable.numeric && summaryLabel(variable) && !loading && (
                                            <Tooltip title="Cada punto del gráfico resume ese intervalo">
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {summaryLabel(variable)}
                                                </Typography>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        {formatMeasurementValue(variable.latest?.value, {
                                            unit: variable.unit || "",
                                            decimals: variable.numeric ? variable.decimals : -1,
                                        })}
                                    </Typography>
                                </Stack>
                                <Box sx={{ mt: 0.5 }}>
                                    {variable.numeric
                                        ? <TimeSeriesChart data={variable.data} unit={variable.unit || ""} decimals={variable.decimals} />
                                        : renderTextTimeline(variable)}
                                </Box>
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            </Box>
        </Drawer>
    );
};

export default DeviceDetailPanel;
